<template>
  <v-card>
    <!-- ══ TITLE BAR ══ -->
    <v-card-title class="flex-wrap" style="gap: 8px;">
      <span class="me-3 text-subtitle-1 font-weight-bold">({{ njangi_groups.length }}) Njangi Groups</span>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
        dense
        style="max-width: 220px; min-width: 140px;"
      ></v-text-field>
    </v-card-title>

    <!-- ══ FILTRES ══ -->
    <div class="d-flex flex-wrap align-center pa-2" style="gap: 8px;">
      <v-select
        v-model="selectedCycle"
        :items="cycleOptions"
        label="Cycle"
        dense
        outlined
        hide-details
        style="max-width: 160px; min-width: 120px;"
      ></v-select>

      <v-select
        v-model="selectedAmounts"
        :items="amountOptions"
        label="Amount"
        multiple
        chips
        deletable-chips
        dense
        outlined
        hide-details
        style="max-width: 260px; min-width: 160px;"
      ></v-select>

      <v-btn x-small text color="grey" @click="resetFilters">
        <v-icon left x-small>mdi-filter-off</v-icon> Reset
      </v-btn>
    </div>

    <!-- ══ TABLE — colonnes réduites sur mobile ══ -->
    <v-data-table
      :headers="responsiveHeaders"
      :items="filteredGroups"
      item-key="id"
      class="table-rounded"
      :items-per-page="10"
      disable-sort
      mobile-breakpoint="600"
    >
      <template #[`item.name`]="{item}">
        <span
          style="cursor: pointer; max-width: 180px;"
          @click.stop="openGroupDetails(item.id)"
          class="d-block font-weight-semibold text--primary text-truncate"
        >
          {{ item.name }}
        </span>
      </template>

      <template #[`item.current_cycle`]="{item}">
        <v-chip small :color="item.is_active ? 'primary' : 'grey'" outlined>
          <v-icon left x-small>mdi-recycle</v-icon>
          {{ item.current_cycle }}
        </v-chip>
      </template>

      <template #[`item.id`]="{item}">
        <v-btn icon x-small color="primary" @click="goToChat(item.id)">
          <i class="fa-solid fa-comments"></i>
        </v-btn>
      </template>

      <template #[`item.edit`]="{item}">
        <v-btn
          icon x-small color="primary"
          @click.stop="$router.push({ name: 'njangi.edit_group', params: { id: item.id } })"
        >
          <i class="fa-solid fa-edit"></i>
        </v-btn>
      </template>
    </v-data-table>

    <!-- ══════════════════════════════════════════════════════ -->
    <!-- DIALOG PRINCIPAL — DETAILS DU GROUPE                  -->
    <!-- ══════════════════════════════════════════════════════ -->
    <v-dialog
      v-model="dialog"
      :max-width="$vuetify.breakpoint.smAndDown ? '100%' : '920px'"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      content-class="njangi-dialog-elevated"
      scrollable
    >
      <!-- Loading -->
      <v-card v-if="loading" class="loading-card-elevated">
        <div class="text-center pa-8">
          <font-awesome-icon icon="fa-solid fa-spinner" class="spinner-anim" style="font-size: 48px; color: var(--v-primary-base);"/>
          <p class="mt-4 text-subtitle-2 primary--text font-weight-medium">Loading group details…</p>
        </div>
      </v-card>

      <!-- MAIN CARD -->
      <v-card
        v-else-if="!delete_njangi && !activate_njangi && !manage_positions && !reset_cycle_dialog && !show_cycle_history && !loading"
        class="njangi-main-card"
      >
        <!-- Hero Header -->
        <div class="njangi-hero-header">
          <div class="hero-overlay" style="padding: 14px 16px 18px 16px; position: relative;">
            <v-btn
              icon color="white"
              @click="resetDialogState"
              class="close-btn"
              style="position: absolute; top: 10px; right: 10px; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.3); z-index: 3;"
              small
            >
              <v-icon small>mdi-close</v-icon>
            </v-btn>

            <div style="padding-right: 48px;">
              <div class="d-flex align-center mb-2">
                <div class="hero-icon-wrap mr-3">
                  <i class="fa-solid fa-users" style="font-size: 22px; color: white;"></i>
                </div>
                <div class="hero-title-block">
                  <div class="text-caption white--text text-uppercase opacity-70 mb-1">Njangi Group</div>
                  <h2 class="text-subtitle-1 text-sm-h6 white--text font-weight-bold mb-0" style="line-height: 1.2;">
                    {{ njangi_group_name }}
                  </h2>
                </div>
              </div>

              <div class="d-flex align-center flex-wrap mt-2" style="gap: 6px;">
                <v-chip x-small :color="is_active ? 'success' : 'grey darken-2'" text-color="white" class="elevation-1 status-chip">
                  <v-icon left x-small>{{ is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                  {{ is_active ? 'Active' : 'Inactive' }}
                </v-chip>
                <v-chip v-if="current_cycle" x-small color="white" class="elevation-1 font-weight-bold" style="color: var(--v-primary-base, #1976d2) !important;">
                  <v-icon left x-small style="color: var(--v-primary-base, #1976d2) !important;">mdi-recycle</v-icon> Cycle {{ current_cycle }}
                </v-chip>
                <v-chip v-if="collecting_cycle" x-small color="white" class="elevation-1" style="color: #0288d1 !important;">
                  <v-icon left x-small style="color: #0288d1 !important;">mdi-calendar-sync</v-icon> {{ collecting_cycle }}
                </v-chip>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Bar -->
        <div class="njangi-action-bar pa-2 pa-sm-4">
          <div class="action-scroll-row">
            <v-btn x-small rounded color="deep-orange" dark @click="reset_cycle_dialog = true" elevation="2" class="action-btn">
              <v-icon left x-small>mdi-rotate-left</v-icon> Reset
            </v-btn>
            <v-btn x-small rounded color="blue-grey" dark @click="loadCycleVersions" :loading="loading_versions" elevation="2" class="action-btn">
              <v-icon left x-small>mdi-history</v-icon> History
            </v-btn>
            <v-btn x-small rounded color="primary" dark @click="$router.push({ name: 'njangi.edit_group', params: { id: njangi_id } })" elevation="2" class="action-btn">
              <v-icon left x-small>mdi-pencil</v-icon> Edit
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn v-if="is_active" x-small rounded color="error" dark @click="delete_njangi = true" elevation="2" class="action-btn">
              <v-icon left x-small>mdi-close-circle</v-icon> Deactivate
            </v-btn>
            <v-btn v-else x-small rounded color="success" dark @click="activate_njangi = true" elevation="2" class="action-btn">
              <v-icon left x-small>mdi-check-circle</v-icon> Activate
            </v-btn>
          </div>
        </div>

        <v-card-text class="pa-3 pa-sm-5 pt-4">

          <!-- 🆕 GRACE PERIOD BANNER -->
          <div
            v-if="collecting_cycle === 'Weekly' && isGracePeriod"
            class="mgr-grace-banner mb-4"
          >
            <div class="mgr-grace-inner">
              <div class="mgr-grace-left">
                <v-icon color="white" class="mr-3" size="20">lock_clock</v-icon>
                <div>
                  <div class="mgr-grace-title">
                    Catch-up Days
                    <span class="mgr-grace-chip">Penalty Window</span>
                  </div>

                </div>
              </div>
              <div class="mgr-grace-countdown">
                <div class="mgr-ct-block">
                  <span class="mgr-ct-val">{{ gracePeriodCountdown.days }}</span>
                  <span class="mgr-ct-unit">d</span>
                </div>
                <span class="mgr-ct-sep">:</span>
                <div class="mgr-ct-block">
                  <span class="mgr-ct-val">{{ gracePeriodCountdown.hours }}</span>
                  <span class="mgr-ct-unit">h</span>
                </div>
                <span class="mgr-ct-sep">:</span>
                <div class="mgr-ct-block">
                  <span class="mgr-ct-val">{{ gracePeriodCountdown.minutes }}</span>
                  <span class="mgr-ct-unit">m</span>
                </div>
                <span class="mgr-ct-sep">:</span>
                <div class="mgr-ct-block">
                  <span class="mgr-ct-val">{{ gracePeriodCountdown.seconds }}</span>
                  <span class="mgr-ct-unit">s</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Info & Collector -->
          <v-row class="mb-4" dense>
            <v-col cols="12" md="8">
              <v-card outlined class="info-card pa-3" style="border-radius: 12px;">
                <div class="text-overline text--secondary mb-1 d-flex align-center" style="font-size: 10px;">
                  <v-icon x-small color="primary" class="mr-1">mdi-information</v-icon>
                  DESCRIPTION
                </div>
                <div class="body-2 text--primary mb-2" v-html="njangi_group_description || '<em class=\'grey--text\'>No description provided.</em>'"></div>
                <v-divider class="my-2"></v-divider>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <div class="info-row">
                      <v-icon small color="primary" class="mr-1">mdi-phone</v-icon>
                      <span class="caption"><strong>Contact:</strong> {{ njangi_contact || '—' }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div class="info-row">
                      <v-icon small color="primary" class="mr-1">mdi-cash-multiple</v-icon>
                      <span class="caption"><strong>Contribution:</strong> {{ formatAmount(contribution_amount) }} F CFA <span class="grey--text">/ {{ collecting_cycle }}</span></span>
                    </div>
                  </v-col>
                  <v-col v-if="!collection_activated" cols="12" sm="6">
                    <div class="info-row">
                      <v-icon small color="primary" class="mr-1">mdi-calendar-start</v-icon>
                      <span class="caption"><strong>Start:</strong> {{ start_date }}</span>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>

            <!-- Collector Card -->
            <v-col cols="12" md="4">
              <v-card outlined class="collector-card pa-3 text-center d-flex flex-row flex-md-column align-center justify-start justify-md-center" style="border-radius: 12px; min-height: 80px;">
                <div class="collector-avatar mr-3 mr-md-0 mb-md-2">
                  <v-icon size="32" :color="collecting_cycle === 'Weekly' && isGracePeriod ? 'grey' : 'primary'">mdi-account-star</v-icon>
                </div>
                <div>
                  <div v-if="collecting_cycle === 'Weekly' && isGracePeriod" class="text-overline text--secondary mb-0" style="font-size: 9px;">LAST COLLECTOR</div>
                  <div v-else class="text-overline text--secondary mb-0" style="font-size: 9px;">CURRENT COLLECTOR</div>

                  <div class="text-subtitle-2 font-weight-bold text-truncate nav-link" :class="collecting_cycle === 'Weekly' && isGracePeriod ? 'grey--text' : 'primary--text'"
                    :title="`Open payment for ${visualCollectorName}`"
                    @click.stop="goToCollectorPayment(visualCollectorName, njangi_group_name)">
                    {{ visualCollectorName || '—' }}
                    <v-icon v-if="visualCollectorName && visualCollectorName !== '—'" x-small class="ml-1">mdi-cash-multiple</v-icon>
                  </div>

                  <v-chip v-if="visualCollectorPosition" x-small :color="collecting_cycle === 'Weekly' && isGracePeriod ? 'grey' : 'orange'" dark class="mt-1">
                    <v-icon left x-small>mdi-crown</v-icon>
                    Position #{{ visualCollectorPosition }}
                  </v-chip>

                  <div v-if="collecting_cycle === 'Weekly' && isGracePeriod" class="mt-2">
                    <v-chip x-small color="deep-orange lighten-4" class="deep-orange--text font-weight-bold" style="font-size:9px;">
                      last cycle
                    </v-chip>
                  </div>

                  <div v-if="collecting_cycle === 'Weekly' && isGracePeriod && current_collector" class="mt-2 pa-2 rounded" style="background:#e3f0ff; font-size:10px; color:#1565c0; line-height:1.4;">
                    <v-icon x-small color="#1565c0">arrow_forward</v-icon>
                    From <strong>{{ nextWindowDayLabel }}</strong>:<br>
                    <strong>{{ current_collector }}</strong> #{{ current_collect_position }}
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Stats Grid -->
          <v-row dense class="mb-4">
            <v-col cols="6" sm="3">
              <v-card class="stat-card pa-2 text-center" outlined :class="{'stat-success': displayContributedCount === displayTotalMembers && displayTotalMembers > 0}">
                <v-icon color="success" size="26" class="mb-1">mdi-check-circle</v-icon>
                <div class="text-h6 font-weight-bold success--text">{{ displayContributedCount }}</div>
                <div class="caption text--secondary">Contributed</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card class="stat-card pa-2 text-center" outlined :class="{'stat-error': displayNotContributedCount > 0}">
                <v-icon color="error" size="26" class="mb-1">mdi-close-circle</v-icon>
                <div class="text-h6 font-weight-bold error--text">{{ displayNotContributedCount }}</div>
                <div class="caption text--secondary">Not Contributed</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card class="stat-card pa-2 text-center" outlined>
                <v-icon color="primary" size="26" class="mb-1">mdi-cash</v-icon>
                <div class="text-h6 font-weight-bold primary--text" style="font-size: 0.95rem !important;">{{ formatAmount(displayContributedAmount) }}</div>
                <div class="caption text--secondary">FCFA Collected</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card class="stat-card pa-2 text-center" outlined :class="{'stat-success': contributionPercentage === 100}">
                <v-icon :color="contributionPercentage === 100 ? 'success' : 'warning'" size="26" class="mb-1">mdi-percent-box</v-icon>
                <div class="text-h6 font-weight-bold" :class="contributionPercentage === 100 ? 'success--text' : 'warning--text'">{{ contributionPercentage }}%</div>
                <div class="caption text--secondary">Progress</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Progress Bar -->
          <v-card class="mb-4 pa-3" outlined style="border-radius: 12px;">
            <div class="d-flex justify-space-between align-center mb-2 flex-wrap" style="gap: 4px;">
              <div class="d-flex align-center">
                <v-icon color="primary" small class="mr-1">mdi-chart-bar</v-icon>
                <span class="caption font-weight-bold text--primary">Collection Progress</span>
              </div>
              <span class="caption font-weight-bold" :class="displayContributedAmount >= displayExpectedAmount ? 'success--text' : 'primary--text'">
                {{ formatAmount(displayContributedAmount) }} / {{ formatAmount(displayExpectedAmount) }} F CFA
              </span>
            </div>
            <v-progress-linear
              :value="contributionPercentage"
              height="24"
              rounded
              :color="contributionPercentage === 100 ? 'success' : 'primary'"
              striped
              class="progress-animated"
            >
              <template v-slot:default>
                <strong class="white--text" style="font-size: 12px; text-shadow: 0 1px 2px rgba(0,0,0,0.3);">{{ contributionPercentage }}%</strong>
              </template>
            </v-progress-linear>
            <div v-if="collection_activated" class="mt-2 d-flex align-center justify-end">
              <v-icon x-small color="grey" class="mr-1">mdi-calendar</v-icon>
              <span class="caption grey--text">Collection Date: {{ collection_date }}</span>
            </div>
          </v-card>

          <!-- Requested Transactions -->
          <v-card v-if="njangiRequestedTransactions.length > 0" class="mb-4" outlined style="border-radius: 12px; overflow: hidden;">
            <div class="pa-3 d-flex align-center" style="background: #f8f9fa; border-bottom: 1px solid #e0e0e0;">
              <v-icon left color="primary" small class="mr-2">mdi-swap-horizontal</v-icon>
              <span class="caption font-weight-bold text--primary">Requested Transactions</span>
              <v-spacer></v-spacer>
              <v-chip x-small color="primary" text-color="white" class="font-weight-bold">{{ njangiRequestedTransactions.length }}</v-chip>
            </div>
            <div class="pa-2" style="max-height: 200px; overflow-y: auto;">
              <v-list dense class="transparent">
                <v-list-item
                  v-for="item in njangiRequestedTransactions"
                  :key="item.id"
                  class="transaction-item mb-1 rounded-lg"
                  :class="item.withdrawal ? 'withdrawal-row' : 'deposit-row'"
                  style="min-height: 52px;"
                >
                  <v-list-item-avatar size="32" class="mr-2">
                    <v-avatar :color="item.withdrawal ? 'error lighten-4' : 'success lighten-4'" size="32">
                      <v-icon :color="item.withdrawal ? 'error' : 'success'" x-small>
                        {{ item.withdrawal ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold' }}
                      </v-icon>
                    </v-avatar>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="caption font-weight-medium d-flex align-center flex-wrap" style="gap: 4px;">
                      <span :class="item.withdrawal ? 'error--text' : 'success--text'">{{ item.withdrawal ? 'Interrupt' : 'Deposit' }}</span>
                      <span class="font-weight-bold">{{ formatAmount(item.amount) }} FCFA</span>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      <v-chip x-small :color="item.confirmed ? 'success' : item.cancelled ? 'grey' : 'warning'" text-color="white">
                        {{ item.confirmed ? 'Confirmed' : item.cancelled ? 'Cancelled' : 'Pending' }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <span class="caption grey--text">{{ item.get_date_str }}</span>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </div>
          </v-card>

          <!-- Members Section -->
          <v-card outlined class="member-section" style="border-radius: 12px; overflow: hidden;">
            <div class="member-header pa-3 d-flex flex-column" style="gap: 10px;">
              <div class="d-flex align-center justify-space-between white--text">
                <div class="d-flex align-center">
                  <v-icon color="white" small class="mr-1">mdi-account-group</v-icon>
                  <span class="font-weight-bold caption">Members & Contributions</span>
                  <v-chip x-small color="white" text-color="primary" class="ml-2 font-weight-bold">{{ displayTotalMembers }}</v-chip>
                </div>
              </div>
              <div class="d-flex flex-wrap" style="gap: 6px;">
                <v-btn x-small color="success" dark @click="openInviteDialog" elevation="2" rounded>
                  <v-icon left x-small>mdi-account-plus</v-icon> Invite
                </v-btn>
                <v-btn x-small color="info" dark @click="openInvitationsDialog" elevation="2" rounded>
                  <v-icon left x-small>mdi-email-open</v-icon> Pending
                </v-btn>
                <v-btn x-small color="white" class="primary--text" @click="manage_positions = true" elevation="2" rounded>
                  <v-icon left x-small>mdi-cog</v-icon> Positions
                </v-btn>
              </div>
            </div>

            <!-- Date Selector -->
            <div class="date-selector-bar pa-3 grey lighten-4">
              <v-row align="center" dense>
                <v-col cols="8" sm="7">
                  <div class="date-selector-wrapper">
                    <!-- Daily -->
                    <v-menu
                      v-if="collecting_cycle === 'Daily'"
                      v-model="dateMenuDaily"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formatDateDisplay(date_history)"
                          label="Select Date"
                          prepend-inner-icon="mdi-calendar"
                          readonly v-bind="attrs" v-on="on"
                          outlined dense hide-details
                          class="date-field" background-color="white"
                        ></v-text-field>
                      </template>
                      <v-date-picker v-model="date_history" @input="dateMenuDaily = false; handleDateChange()" no-title scrollable></v-date-picker>
                    </v-menu>

                    <!-- Weekly -->
                    <div v-else-if="collecting_cycle === 'Weekly'" class="week-selector-container">
                      <v-text-field
                        :value="formatWeekDisplay(date_history)"
                        label="Select Week" prepend-inner-icon="mdi-calendar-range"
                        readonly outlined dense hide-details
                        @click="openWeekPicker" class="date-field" background-color="white"
                      ></v-text-field>
                      <input ref="weekInput" type="week" v-model="date_history" @change="handleDateChange"
                        style="position: absolute; opacity: 0; width: 0; height: 0; pointer-events: none;" />
                    </div>

                    <!-- Monthly -->
                    <div v-else-if="collecting_cycle === 'Monthly'" class="month-selector-container">
                      <v-text-field
                        :value="formatMonthDisplay(date_history)"
                        label="Select Month" prepend-inner-icon="mdi-calendar-month"
                        readonly outlined dense hide-details
                        @click="openMonthPicker" class="date-field" background-color="white"
                      ></v-text-field>
                      <input ref="monthInput" type="month" v-model="date_history" @change="handleDateChange"
                        style="position: absolute; opacity: 0; width: 0; height: 0; pointer-events: none;" />
                    </div>
                  </div>
                </v-col>
                <v-col cols="4" sm="5" class="text-right text-sm-center">
                  <v-btn color="primary" @click="refreshHistory" :loading="refreshing" x-small rounded elevation="2">
                    <v-icon left x-small>mdi-refresh</v-icon>
                    <span class="hidden-xs-only">Refresh</span>
                  </v-btn>
                </v-col>
              </v-row>
            </div>

            <!-- Summary Pills -->
            <div class="pa-3 pb-2">
              <v-row dense class="mb-2">
                <v-col cols="6" class="text-center">
                  <div class="stat-pill success">
                    <v-icon x-small color="white" class="mr-1">mdi-check</v-icon>
                    <span class="white--text font-weight-bold" style="font-size: 12px;">{{ displayContributedCount }} Contributed</span>
                  </div>
                </v-col>
                <v-col cols="6" class="text-center">
                  <div class="stat-pill error">
                    <v-icon x-small color="white" class="mr-1">mdi-close</v-icon>
                    <span class="white--text font-weight-bold" style="font-size: 12px;">{{ displayNotContributedCount }} Not Contributed</span>
                  </div>
                </v-col>
              </v-row>

              <v-progress-linear
                :value="contributionPercentage" height="20" rounded
                :color="contributionPercentage === 100 ? 'success' : 'primary'"
                class="mb-2" striped
              >
                <strong class="white--text" style="font-size: 11px; text-shadow: 0 1px 2px rgba(0,0,0,0.3);">{{ contributionPercentage }}%</strong>
              </v-progress-linear>

              <div class="text-center mb-2">
                <small class="font-weight-bold caption">
                  <v-icon small :color="displayContributedAmount >= displayExpectedAmount ? 'success' : 'warning'">
                    {{ displayContributedAmount >= displayExpectedAmount ? 'mdi-check-circle' : 'mdi-alert' }}
                  </v-icon>
                  {{ formatAmount(displayContributedAmount) }} / {{ formatAmount(displayExpectedAmount) }} F CFA
                </small>
              </div>

              <v-divider class="mb-2"></v-divider>

              <!-- Members List -->
              <v-list dense class="transparent member-list">
                <v-list-item
                  v-for="item in njangi_members"
                  :key="item.membership_id"
                  class="member-item mb-1 rounded-lg px-2"
                  :class="item.contributed ? 'success-bg' : 'error-bg'"
                  style="min-height: 48px;"
                >
                  <v-list-item-avatar size="32" class="mr-2">
                    <v-avatar :color="item.contributed ? 'success' : 'error'" size="32">
                      <v-icon color="white" x-small>{{ item.contributed ? 'mdi-check' : 'mdi-close' }}</v-icon>
                    </v-avatar>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title class="caption font-weight-bold d-flex align-center flex-wrap" style="gap: 4px; font-size: 13px !important;">
                      {{ item.member_name }}
                      <v-chip v-if="item.position && item.position !== 9999" x-small color="primary" text-color="white">#{{ item.position }}</v-chip>
                    </v-list-item-title>

                    <v-list-item-subtitle class="mt-1 d-flex flex-wrap align-center" style="gap: 4px;">
                      <v-chip x-small :color="item.contributed ? 'success' : 'error'" text-color="white">
                        {{ item.contributed ? '✅ Contributed' : '❌ Not Contributed' }}
                      </v-chip>
                      <v-chip v-if="item.has_penalty" x-small :color="item.penalty_paid ? 'warning' : 'error'" text-color="white">
                        <v-icon x-small left>mdi-alert</v-icon>
                        {{ item.penalty_paid ? 'Penalty Paid' : `Penalty: ${formatAmount(item.penalty_amount)}` }}
                      </v-chip>
                      <v-chip v-if="item.contribution_source === 'penalty_payment'" x-small color="info" text-color="white">
                        Via Penalty
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>

              <div v-if="!njangi_members || njangi_members.length === 0" class="text-center pa-5 grey--text empty-state">
                <v-icon size="48" color="grey lighten-1">mdi-account-group</v-icon>
                <p class="mt-2 caption grey--text">No contribution data for this period</p>
                <p class="caption grey--text" style="font-size: 11px;">Select a different date or refresh</p>
              </div>
            </div>
          </v-card>
        </v-card-text>
      </v-card>

      <!-- ═══════ Dialog gestion des positions ═══════ -->
      <v-card v-else-if="manage_positions && !loading" class="manage-card">
        <v-card-title class="primary white--text d-flex justify-space-between align-center pa-3">
          <span class="d-flex align-center caption font-weight-bold">
            <v-icon color="white" small class="mr-2">mdi-sort</v-icon>
            Manage Positions
          </span>
          <v-btn icon small color="white" @click="manage_positions = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-3 pa-sm-5">
          <v-alert type="info" dense class="mb-3 rounded-lg" outlined>
            <span class="caption">Tap position numbers to change collection order.</span>
          </v-alert>

          <v-list class="transparent">
            <v-list-item
              v-for="(member, index) in member_positions"
              :key="member.id"
              class="position-item mb-2 rounded-xl px-2"
              :class="member.position === current_collect_position ? 'current-collector-row' : ''"
              style="min-height: 56px;"
            >
              <v-list-item-avatar class="mr-2">
                <v-chip
                  small
                  :color="member.position === current_collect_position ? 'success' : 'primary'"
                  text-color="white"
                  style="cursor: pointer; font-weight: bold;"
                  @click="openPositionDialog(member)"
                >
                  {{ member.position }}
                </v-chip>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="caption font-weight-bold d-flex align-center flex-wrap" style="gap: 4px; font-size: 13px !important;">
                  {{ member.member_name }}
                  <v-chip v-if="member.position === current_collect_position" x-small color="success" text-color="white">
                    <v-icon left x-small>mdi-star</v-icon> Collector
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle class="caption">
                  Status: {{ member.status }} · {{ member.name || 'Member' }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action class="d-flex flex-row align-center" style="gap: 4px;">
                <v-btn x-small color="success" dark @click.stop="openMakeContribution(member)" elevation="1" icon>
                  <v-icon x-small>mdi-cash-plus</v-icon>
                </v-btn>
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small v-bind="attrs" v-on="on" color="grey">
                      <v-icon x-small>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense class="py-1">
                    <v-list-item @click="openPositionDialog(member)" class="rounded-lg">
                      <v-list-item-icon class="mr-2"><v-icon small color="primary">mdi-pencil</v-icon></v-list-item-icon>
                      <v-list-item-title class="caption">Change Position</v-list-item-title>
                    </v-list-item>
                    <v-divider class="my-1"></v-divider>
                    <v-list-item @click="confirmRemoveMember(member)" class="red--text rounded-lg">
                      <v-list-item-icon class="mr-2"><v-icon small color="error">mdi-trash-can</v-icon></v-list-item-icon>
                      <v-list-item-title class="caption error--text">Remove Member</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="primary" small rounded @click="manage_positions = false" elevation="2">
            <v-icon left small>mdi-check</v-icon> Done
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- ═══════ Dialog désactivation ═══════ -->
      <v-card v-else-if="delete_njangi && !loading" class="confirm-card">
        <div class="confirm-header error">
          <v-icon size="40" color="white" class="mb-2">mdi-close-circle</v-icon>
          <h3 class="white--text text-subtitle-1 font-weight-bold">Deactivate Njangi Group</h3>
        </div>
        <v-card-text class="text-center pa-4">
          <h2 class="font-weight-bold error--text mb-3 text-subtitle-1">{{ njangi_group_name }}</h2>
          <v-alert type="info" class="mb-3 text-left rounded-lg" outlined dense>
            <div class="caption font-weight-bold mb-1">ℹ️ This will:</div>
            <ul class="ml-3 mb-1" style="font-size: 12px;">
              <li>Mark the group as <strong>inactive</strong></li>
              <li>Hide it from the active groups list</li>
            </ul>
            <div class="success--text font-weight-bold caption">
              <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
              All data, history and transactions are preserved.
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="grey" text @click="delete_njangi = false" small rounded>
            <v-icon left small>mdi-close</v-icon> Cancel
          </v-btn>
          <v-btn color="error" @click="deactivateNjangi(njangi_id)" :loading="loading" small rounded elevation="2">
            <v-icon left small>mdi-close-circle</v-icon> Deactivate
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- ═══════ Dialog activation ═══════ -->
      <v-card v-else-if="activate_njangi && !loading" class="confirm-card">
        <div class="confirm-header success">
          <v-icon size="40" color="white" class="mb-2">mdi-check-circle</v-icon>
          <h3 class="white--text text-subtitle-1 font-weight-bold">Activate Njangi Group</h3>
        </div>
        <v-card-text class="text-center pa-4">
          <h2 class="font-weight-bold success--text mb-3 text-subtitle-1">{{ njangi_group_name }}</h2>
          <v-alert type="success" class="mb-3 text-left rounded-lg" outlined dense>
            <div class="caption font-weight-bold mb-1">✅ This will:</div>
            <ul class="ml-3 mb-1" style="font-size: 12px;">
              <li>Mark the group as <strong>active</strong> again</li>
              <li>Restore all member access</li>
            </ul>
            <div class="info--text font-weight-bold caption">
              <v-icon small color="info" class="mr-1">mdi-information</v-icon>
              All data and transactions are preserved.
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="grey" text @click="activate_njangi = false" small rounded>
            <v-icon left small>mdi-close</v-icon> Cancel
          </v-btn>
          <v-btn color="success" @click="activateNjangi(njangi_id)" :loading="loading" small rounded elevation="2">
            <v-icon left small>mdi-check-circle</v-icon> Activate
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══════ Dialog Reset Cycle ═══════ -->
    <v-dialog v-model="reset_cycle_dialog" :max-width="$vuetify.breakpoint.xsOnly ? '95%' : '520px'" content-class="njangi-dialog">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="deep-orange white--text pa-3">
          <v-icon left small color="white">mdi-rotate-left</v-icon>
          <span class="caption font-weight-bold">Reset Collection Cycle</span>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-alert type="info" dense class="mb-3 rounded-lg" outlined>
            <div class="caption font-weight-bold mb-1">What this does:</div>
            <ul class="ml-3 mt-1" style="font-size: 12px;">
              <li>Restarts collection from <strong>position #1</strong></li>
              <li>Creates a new version snapshot</li>
              <li>No penalties for periods before today</li>
            </ul>
          </v-alert>
          <v-alert type="success" dense class="mb-3 rounded-lg" outlined>
            <span class="caption"><strong>Payment history and member data are NOT deleted.</strong></span>
          </v-alert>
          <v-textarea
            v-model="reset_reason"
            label="Reason for reset (optional)"
            outlined dense rows="3"
            placeholder="e.g. End of year, new cycle..."
            hide-details class="rounded-lg"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="grey" text @click="reset_cycle_dialog = false; reset_reason = ''" small rounded>
            <v-icon left small>mdi-close</v-icon> Cancel
          </v-btn>
          <v-btn color="deep-orange" dark @click="resetGroupCycle" :loading="resetting_cycle" small rounded elevation="2">
            <v-icon left small>mdi-rotate-left</v-icon> Confirm Reset
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══════ Dialog Cycle History ═══════ -->
    <v-dialog v-model="show_cycle_history" :max-width="$vuetify.breakpoint.xsOnly ? '95%' : '600px'" content-class="njangi-dialog">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="primary white--text pa-3">
          <v-icon left small color="white">mdi-history</v-icon>
          <span class="caption font-weight-bold">Cycle Version History</span>
        </v-card-title>
        <v-card-text class="pa-4">
          <div v-if="cycle_versions.length === 0" class="text-center pa-5 grey--text">
            <v-icon size="48" color="grey lighten-2">mdi-history</v-icon>
            <p class="mt-2 caption">No version history yet</p>
          </div>
          <v-timeline dense v-else align-top>
            <v-timeline-item
              v-for="v in cycle_versions" :key="v.id"
              :color="v.end_date === 'Current' ? 'success' : 'grey'" small fill-dot
            >
              <v-card flat outlined class="pa-2 rounded-lg version-card" :class="v.end_date === 'Current' ? 'current-version' : ''">
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="caption font-weight-bold primary--text">Version {{ v.version_number }}</span>
                  <v-chip x-small :color="v.end_date === 'Current' ? 'success' : 'grey'" text-color="white">
                    {{ v.end_date === 'Current' ? '🟢 Current' : 'Archived' }}
                  </v-chip>
                </div>
                <div class="caption grey--text">
                  {{ v.member_count }} members · {{ v.collecting_cycle }} · {{ formatAmount(v.contribution_amount) }} FCFA
                </div>
                <div v-if="v.reset_reason" class="mt-1 caption grey--text font-italic pa-1 rounded" style="background: #f5f5f5; font-size: 11px;">
                  "{{ v.reset_reason }}" — {{ v.reset_by }}
                </div>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="primary" small rounded text @click="show_cycle_history = false">
            <v-icon left small>mdi-close</v-icon> Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══════ Dialog position ═══════ -->
    <v-dialog v-model="position_dialog" :max-width="$vuetify.breakpoint.xsOnly ? '95%' : '480px'" content-class="njangi-dialog">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="primary white--text pa-3">
          <v-icon left small color="white">mdi-swap-horizontal</v-icon>
          <span class="caption font-weight-bold">Change Member Position</span>
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="text-center mb-4">
            <v-avatar color="primary lighten-2" size="64" class="mb-2 elevation-2">
              <span class="white--text text-h5 font-weight-bold">
                {{ selected_member && selected_member.member_name && selected_member.member_name.charAt(0).toUpperCase() }}
              </span>
            </v-avatar>
            <h3 class="font-weight-bold text-subtitle-1">{{ selected_member && selected_member.member_name }}</h3>
            <p class="grey--text caption mb-0">Current Position: <v-chip x-small color="primary" text-color="white">#{{ selected_member && selected_member.position }}</v-chip></p>
          </div>
          <v-text-field
            v-model="new_position" label="New Position" type="number"
            :min="1" :max="member_positions.length"
            outlined dense prepend-inner-icon="mdi-sort-numeric"
            :error="!!position_error" :error-messages="position_error"
            class="rounded-lg" background-color="white"
          ></v-text-field>
          <v-alert v-if="new_position && getPositionTaker()" type="info" dense class="rounded-lg caption" outlined>
            Position {{ new_position }} is held by <strong>{{ getPositionTaker() && getPositionTaker().member_name }}</strong>. Positions will be swapped.
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closePositionDialog" small rounded>Cancel</v-btn>
          <v-btn color="primary" @click="updateMemberPosition" :loading="updating_position"
            :disabled="!new_position || new_position == (selected_member && selected_member.position)"
            small rounded elevation="2"
          >
            <v-icon left small>mdi-content-save</v-icon> Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══════ Dialog Invite ═══════ -->
    <v-dialog v-model="show_invite_dialog" :max-width="$vuetify.breakpoint.xsOnly ? '95%' : '560px'" content-class="njangi-dialog">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="primary white--text pa-3">
          <v-icon left small color="white">mdi-account-plus</v-icon>
          <span class="caption font-weight-bold">Invite Members — {{ njangi_group_name }}</span>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-text-field
            v-model="searchUsername"
            placeholder="Search by username or email"
            outlined dense @input="searchUsers" clearable hide-details
            class="mb-3 rounded-lg" background-color="white"
            prepend-inner-icon="mdi-magnify" :loading="searchingUsers"
          ></v-text-field>
          <v-alert type="info" dense class="rounded-lg caption" outlined>Type at least 2 characters to search</v-alert>

          <div v-if="searchResults.length > 0" class="mt-3" style="max-height: 280px; overflow-y: auto;">
            <v-list dense class="transparent">
              <v-list-item
                v-for="user in searchResults" :key="user.id"
                class="user-result-item mb-2 rounded-xl" @click="inviteUser(user)"
                :disabled="sendingInvitation"
                style="cursor: pointer; border: 1px solid #e0e0e0; min-height: 56px;"
              >
                <v-list-item-avatar size="36" class="mr-2">
                  <v-avatar color="primary lighten-2" size="36">
                    <span class="white--text font-weight-bold">{{ user.username && user.username.charAt(0).toUpperCase() }}</span>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="caption font-weight-bold">{{ user.username }}</v-list-item-title>
                  <v-list-item-subtitle v-if="user.email" class="caption">
                    <v-icon x-small color="grey" class="mr-1">mdi-email</v-icon> {{ user.email }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn x-small color="primary" rounded :loading="sendingInvitation" elevation="2">
                    <v-icon left x-small>mdi-plus</v-icon> Invite
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>

          <div v-else-if="searchUsername && searchUsername.length >= 2 && !searchingUsers" class="text-center pa-4">
            <v-icon size="48" color="grey lighten-2">mdi-account-search</v-icon>
            <p class="grey--text mt-2 caption">No users found</p>
          </div>
          <div v-else-if="!searchUsername || searchUsername.length < 2" class="text-center pa-4">
            <v-icon size="48" color="grey lighten-2">mdi-account-group</v-icon>
            <p class="grey--text mt-2 caption">Start typing to search</p>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeInviteDialog" small rounded>
            <v-icon left small>mdi-close</v-icon> Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══════ Dialog Remove Member ═══════ -->
    <v-dialog v-model="remove_member_dialog" :max-width="$vuetify.breakpoint.xsOnly ? '95%' : '500px'" content-class="njangi-dialog">
      <v-card class="rounded-xl overflow-hidden">
        <div class="confirm-header error pa-3 text-center">
          <v-icon size="36" color="white" class="mb-1">mdi-account-minus</v-icon>
          <h3 class="white--text text-subtitle-2 font-weight-bold">Remove Member from Group</h3>
        </div>
        <v-card-text class="pa-4 text-center">
          <h3 class="font-weight-bold text-subtitle-1 mb-1">{{ member_to_remove && member_to_remove.member_name }}</h3>
          <p class="grey--text caption">Position: {{ member_to_remove && member_to_remove.position }}</p>
          <v-alert type="warning" dense class="mb-2 text-left rounded-lg" outlined>
            <ul class="ml-3" style="font-size: 12px;">
              <li>Removes member permanently</li>
              <li>Adjusts all subsequent positions (-1)</li>
              <li>Cancels any pending transactions</li>
            </ul>
          </v-alert>
          <v-alert type="error" dense class="rounded-lg caption" outlined>
            <strong>This action cannot be undone!</strong>
          </v-alert>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="grey" text @click="remove_member_dialog = false" small rounded>Cancel</v-btn>
          <v-btn color="error" @click="removeMember" :loading="removing_member" small rounded elevation="2">
            <v-icon left small>mdi-trash-can</v-icon> Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══════ Dialog Pending Invitations ═══════ -->
    <v-dialog v-model="show_invitations_dialog" :max-width="$vuetify.breakpoint.xsOnly ? '95%' : '600px'" content-class="njangi-dialog">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="primary white--text pa-3">
          <v-icon left small color="white">mdi-email-open</v-icon>
          <span class="caption font-weight-bold">Pending Invitations — {{ njangi_group_name }}</span>
        </v-card-title>
        <v-card-text class="pa-4">
          <div v-if="loading_invitations" class="text-center pa-5">
            <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
            <p class="mt-2 caption primary--text">Loading…</p>
          </div>
          <div v-else-if="invitations.length === 0" class="text-center pa-5">
            <v-icon size="48" color="grey lighten-2">mdi-inbox</v-icon>
            <p class="grey--text mt-2 caption">No pending invitations</p>
          </div>
          <v-list dense v-else class="transparent">
            <v-list-item
              v-for="inv in invitations" :key="inv.membership_id"
              class="invitation-item mb-2 rounded-xl"
              style="border: 1px solid #e0e0e0; min-height: 56px;"
            >
              <v-list-item-avatar size="36" class="mr-2">
                <v-avatar color="primary lighten-2" size="36">
                  <span class="white--text font-weight-bold">{{ inv.username && inv.username.charAt(0).toUpperCase() }}</span>
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="caption font-weight-bold">{{ inv.username }}</v-list-item-title>
                <v-list-item-subtitle class="caption">
                  <v-icon x-small color="grey" class="mr-1">mdi-clock</v-icon> {{ inv.time_created }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn x-small color="error" rounded :loading="cancelling_invitation === inv.membership_id"
                  @click="cancelInvitation(inv.membership_id)" elevation="2">
                  <v-icon left x-small>mdi-close</v-icon> Cancel
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="primary" small rounded text @click="show_invitations_dialog = false">
            <v-icon left small>mdi-close</v-icon> Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mdiSquareEditOutline, mdiDotsVertical } from '@mdi/js'
import axios from 'axios'

// ════════════════════════════════════════════
// 🧪 TEST MODE — changer 5→6 pour tester
//    la grace period le vendredi
// ════════════════════════════════════════════
const WINDOW_OPEN_DAY = 5

function getWindowOpenDays() {
  return [0, 1, 2, 3].map(i => (WINDOW_OPEN_DAY + i) % 7)
}

export default {
  name: 'NjangiGroupsManager',

  data() {
    return {
      headers: [
        { text: 'Njangi Name', value: 'name' },
        { text: 'Cycle',       value: 'current_cycle' },
        { text: 'Admin',       value: 'admin' },
        { text: 'Contact',     value: 'njangi_contact' },
        { text: 'Date/Time',   value: 'get_date_str' },
        { text: 'Chat',        value: 'id' },
        { text: 'Edit',        value: 'edit', sortable: false },
      ],
      dateMenuDaily: false,
      refreshing: false,
      njangi_groups: [],
      njangi_members: [],
      member_positions: [],
      njangiRequestedTransactions: [],
      search: '',
      selectedStartDate: null,
      selectedEndDate: null,
      loading: false,
      updating_position: false,
      removing_member: false,

      // Dialog states
      dialog: false,
      delete_njangi: false,
      activate_njangi: false,
      manage_positions: false,
      position_dialog: false,
      remove_member_dialog: false,
      reset_cycle_dialog: false,
      reset_reason: '',
      resetting_cycle: false,
      cycle_versions: [],
      show_cycle_history: false,
      loading_versions: false,

      // Group data
      njangi_id: "",
      njangi_group_name: '',
      njangi_contact: '',
      njangi_group_description: '',
      contribution_amount: '',
      collecting_cycle: '',
      no_contributed: null,
      no_members: null,
      date_history: "",
      collection_date: "",
      start_date: "",
      start_date_raw: null,
      collection_activated: false,
      contributed_amount: "",
      expected_amount: "",
      current_collector: "",
      current_collect_position: null,
      history_contributed_amount: "",
      history_expected_amount: "",
      is_active: true,
      current_cycle: 1,

      // Invite
      searchUsername: '',
      searchResults: [],
      searchingUsers: false,
      sendingInvitation: false,
      show_invite_dialog: false,

      // Invitations en attente
      show_invitations_dialog: false,
      invitations: [],
      loading_invitations: false,
      cancelling_invitation: null,

      // Position management
      selected_member: null,
      member_to_remove: null,
      new_position: null,
      position_error: "",

      // Filtres
      selectedCycle: 'All',
      selectedAmounts: [],
      cycleOptions: ['All', 'Daily', 'Weekly', 'Monthly', 'Custom'],

      // 🆕 Grace period
      userPenalties: {
        penalties: [],
        total_penalty: 0,
        total_contribution: 0,
        total_to_pay: 0,
        has_penalties: false
      },
      gracePeriodCountdown: { days: '00', hours: '00', minutes: '00', seconds: '00' },
      graceCountdownInterval: null,

      icons: { mdiSquareEditOutline, mdiDotsVertical },
    }
  },

  mounted() {
    document.title = "Manager: Njangi | List of Groups"
    this.getNjangiGroups()
  },

  computed: {
    responsiveHeaders() {
      if (this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.xsOnly) {
        return [
          { text: 'Name',  value: 'name' },
          { text: 'Cycle', value: 'current_cycle' },
          { text: 'Chat',  value: 'id' },
          { text: 'Edit',  value: 'edit', sortable: false },
        ]
      }
      if (this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.smOnly) {
        return [
          { text: 'Name',    value: 'name' },
          { text: 'Cycle',   value: 'current_cycle' },
          { text: 'Admin',   value: 'admin' },
          { text: 'Chat',    value: 'id' },
          { text: 'Edit',    value: 'edit', sortable: false },
        ]
      }
      return this.headers
    },

    baseFilteredGroups() {
      let result = this.njangi_groups
      if (this.selectedCycle && this.selectedCycle !== 'All') {
        result = result.filter(g => g.collecting_cycle === this.selectedCycle)
      }
      if (this.search) {
        const s = this.search.toLowerCase()
        result = result.filter(g =>
          (g.name || '').toLowerCase().includes(s) ||
          (g.admin || '').toLowerCase().includes(s) ||
          (g.njangi_contact || '').toLowerCase().includes(s)
        )
      }
      return result
    },
    amountOptions() {
      return this.baseFilteredGroups
        .map(g => g.contribution_amount)
        .filter((v, i, self) => v != null && self.indexOf(v) === i)
        .sort((a, b) => a - b)
        .map(a => ({ text: `${a.toLocaleString()} FCFA`, value: a }))
    },
    filteredGroups() {
      let result = this.baseFilteredGroups
      if (this.selectedAmounts.length > 0) {
        result = result.filter(g => this.selectedAmounts.includes(g.contribution_amount))
      }
      return result
    },

    displayContributedCount() {
      return this.njangi_members ? this.njangi_members.filter(m => m.contributed).length : 0
    },
    displayTotalMembers() {
      return this.njangi_members ? this.njangi_members.length : this.no_members || 0
    },
    displayNotContributedCount() {
      return this.displayTotalMembers - this.displayContributedCount
    },
    displayContributedAmount() { return this.history_contributed_amount || 0 },
    displayExpectedAmount()    { return this.history_expected_amount || this.expected_amount || 0 },
    contributionPercentage() {
      if (!this.displayExpectedAmount) return 0
      return Math.round((this.displayContributedAmount / this.displayExpectedAmount) * 100)
    },
    contributionProgress() {
      if (!this.expected_amount) return 0
      return (this.contributed_amount / this.expected_amount) * 100
    },
    isCollectionComplete() { return this.contributionProgress >= 100 },
    currentMemberCount()   { return this.member_positions.length },

    periodCollectorInfo()    { return this.getCollectorForPeriod(this.date_history) },
    periodCollectorName()    { return this.periodCollectorInfo.collector },
    periodCollectorPosition(){ return this.periodCollectorInfo.position },
    previousCollectors()     { return this.periodCollectorInfo.previous || [] },

    monthlyPeriodDisplay() {
      if (this.collecting_cycle !== 'Monthly' || !this.date_history) return ''
      const [year, month] = this.date_history.split('-').map(Number)
      let sm = month - 1, sy = year
      if (sm === 0) { sm = 12; sy -= 1 }
      const s = new Date(sy, sm - 1, 20)
      const e = new Date(year, month - 1, 10)
      const opts = { month: 'short', day: 'numeric' }
      return `${s.toLocaleDateString('en-US', opts)} → ${e.toLocaleDateString('en-US', opts)}`
    },

    // 🆕 Grace period
    isGracePeriod() {
      if (this.collecting_cycle !== 'Weekly') return false
      const day = new Date().getDay()
      return !getWindowOpenDays().includes(day)
    },

    nextWindowDayLabel() {
      const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']
      return days[WINDOW_OPEN_DAY % 7]
    },

    visualCollectorName() {
      if (!this.isGracePeriod) return this.current_collector
      if (!this.current_collect_position || !this.member_positions.length) return this.current_collector
      const total = this.member_positions.length
      const prevPos = this.current_collect_position - 1 <= 0 ? total : this.current_collect_position - 1
      const prev = this.member_positions.find(m => m.position === prevPos)
      return prev?.member_name || this.current_collector
    },

    visualCollectorPosition() {
      if (!this.isGracePeriod) return this.current_collect_position
      if (!this.current_collect_position || !this.member_positions.length) return this.current_collect_position
      const total = this.member_positions.length
      return this.current_collect_position - 1 <= 0 ? total : this.current_collect_position - 1
    },
  },

  watch: {
    dialog(value) {
      if (!value) setTimeout(() => this.resetDialogState(), 300)
    },
    selectedStartDate() { this.getNjangiGroups() },
    selectedEndDate()   { this.getNjangiGroups() },
    selectedCycle()     { this.selectedAmounts = [] },
    search()            { this.selectedAmounts = [] },
  },

  methods: {
    // ── Semaine Njangi
    getNjangiWeekNumber(date) {
      const d = new Date(date)
      d.setHours(0, 0, 0, 0)
      if (d.getDay() === 1) d.setDate(d.getDate() - 1)
      d.setDate(d.getDate() + 4 - (d.getDay() || 7))
      const yearStart = new Date(d.getFullYear(), 0, 1)
      return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
    },
    getMondayOfWeek(year, week) {
      const jan4 = new Date(year, 0, 4)
      const jan4Day = jan4.getDay() || 7
      const monday1 = new Date(jan4)
      monday1.setDate(jan4.getDate() - (jan4Day - 1))
      const target = new Date(monday1)
      target.setDate(monday1.getDate() + (week - 1) * 7)
      return target
    },
    getCurrentWeek() {
      const now = new Date()
      const w = this.getNjangiWeekNumber(now)
      return `${now.getFullYear()}-W${w.toString().padStart(2, '0')}`
    },
    // ✅ Nouvelle méthode pour la semaine d'affichage en période de grâce
    getCurrentWeekForDisplay() {
      if (this.collecting_cycle === 'Weekly' && this.isGracePeriod) {
        const now = new Date()
        const prevWeek = new Date(now)
        prevWeek.setDate(now.getDate() - 7)
        const w = this.getNjangiWeekNumber(prevWeek)
        return `${prevWeek.getFullYear()}-W${w.toString().padStart(2, '0')}`
      }
      return this.getCurrentWeek()
    },
    formatWeekString(weekStr) {
      try {
        const [year, week] = weekStr.split('-W')
        if (!year || !week) return 'Select a week'
        const monday = this.getMondayOfWeek(parseInt(year), parseInt(week))
        const end = new Date(monday); end.setDate(monday.getDate() + 7)
        const opts = { month: 'short', day: 'numeric' }
        const isCurrentWeek = weekStr === this.getCurrentWeek()
        const label = this.isGracePeriod ? `📅 Last Week ${week}` : (isCurrentWeek ? `📅 Week ${week}` : `Week ${week}`)
        return `${label} (${year}): ${monday.toLocaleDateString('en-US', opts)} → ${end.toLocaleDateString('en-US', opts)}`
      } catch (e) { return 'Select a week' }
    },
    formatWeekDisplay(weekStr)   { return this.formatWeekString(weekStr || this.getCurrentWeekForDisplay()) },
    formatMonthDisplay(monthStr) { return this.formatMonthString(monthStr || this.getCurrentMonth()) },
    formatMonthString(monthStr) {
      try {
        const [year, month] = monthStr.split('-')
        if (!year || !month) return 'Select a month'
        const date = new Date(parseInt(year), parseInt(month) - 1, 1)
        const isCurrentMonth = monthStr === this.getCurrentMonth()
        return `${isCurrentMonth ? '📅 ' : ''}${date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}`
      } catch (e) { return 'Select a month' }
    },
    formatDateDisplay(date) {
      if (!date) {
        const today = new Date()
        return `📅 Today: ${today.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`
      }
      const d = new Date(date)
      const isToday = d.toDateString() === new Date().toDateString()
      return `${isToday ? '📅 Today: ' : ''}${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`
    },

    openWeekPicker() {
      this.$nextTick(() => {
        const el = this.$refs.weekInput
        if (el) el.dispatchEvent(new MouseEvent('click', { view: window, bubbles: true, cancelable: true }))
      })
    },
    openMonthPicker() {
      this.$nextTick(() => {
        const el = this.$refs.monthInput
        if (el) el.dispatchEvent(new MouseEvent('click', { view: window, bubbles: true, cancelable: true }))
      })
    },
    handleDateChange() { this.getContribHistory(this.njangi_id) },

    openInviteDialog() {
      this.show_invite_dialog = true
      this.searchUsername = ''
      this.searchResults = []
    },
    async searchUsers() {
      if (!this.searchUsername || this.searchUsername.length < 2) { this.searchResults = []; return }
      this.searchingUsers = true
      try {
        const r = await axios.get('/api/v1/njangi/search-users/', { params: { q: this.searchUsername } })
        this.searchResults = r.data
      } catch (e) { this.searchResults = []; this.handleError('Error searching users', e) }
      finally { this.searchingUsers = false }
    },
    async inviteUser(user) {
      if (!this.njangi_id) return
      this.sendingInvitation = true
      try {
        await axios.post(`/api/v1/manager/njangi/groups/${this.njangi_id}/invite/`, { user_id: user.id, username: user.username })
        this.showSuccess(`✅ Invitation sent to ${user.username}`)
        this.searchUsername = ''; this.searchResults = []
        await this.getMemberPositions(this.njangi_id)
        if (this.show_invitations_dialog) this.getGroupInvitations()
      } catch (e) { this.handleError(e.response?.data?.error || 'Error sending invitation', e) }
      finally { this.sendingInvitation = false }
    },
    closeInviteDialog() { this.show_invite_dialog = false; this.searchUsername = ''; this.searchResults = [] },

    async refreshHistory() {
      this.refreshing = true
      try { await this.getContribHistory(this.njangi_id); this.showSuccess('History refreshed!') }
      catch (e) { this.handleError('Error refreshing history', e) }
      finally { this.refreshing = false }
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '0'
      return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    },

    async getNjangiGroups() {
      const s = this.selectedStartDate || 'null', e = this.selectedEndDate || 'null'
      this.loading = true
      try { const r = await axios.get(`/api/v1/manager/njangi/get/groups/${s}/${e}/`); this.njangi_groups = r.data }
      catch (err) { this.handleError('Error loading njangi groups', err) }
      finally { this.loading = false }
    },

    async openGroupDetails(groupId) {
      this.dialog = true
      await this.getNjangiGroupSetting(groupId)
    },

    getCurrentMonth() {
      const now = new Date(), day = now.getDate()
      let m = now.getMonth() + 1, y = now.getFullYear()
      if (day >= 20) { m += 1; if (m > 12) { m = 1; y += 1 } }
      return `${y}-${m.toString().padStart(2, '0')}`
    },
    getCurrentDate() { return new Date().toISOString().split('T')[0] },

    async getNjangiGroupSetting(group_id) {
      this.loading = true
      try {
        const r = await axios.get(`/api/v1/njangi/get/user/group/setting/detail/${group_id}/`)
        const d = r.data
        this.njangi_id                  = d[1].njangi_group_id
        this.njangi_group_name          = d[1].njangi_group_name
        this.njangi_group_description   = d[1].njangi_group_description
        this.njangi_contact             = d[1].njangi_contact
        this.contribution_amount        = d[1].contribution_amount
        this.collecting_cycle           = d[1].collecting_cycle
        this.no_contributed             = d[1].get_no_contribution_members
        this.no_members                 = d[1].get_no_members
        this.contributed_amount         = d[1].contributed_amount
        this.expected_amount            = d[1].expected_amount
        this.current_collector          = d[1].get_current_collector
        this.current_collect_position   = d[1].current_collect_position
        this.collection_date            = d[1].get_collection_date
        this.start_date                 = d[1].get_start_date
        this.start_date_raw             = d[1].start_date
        this.collection_activated       = d[1].collection_activated
        this.is_active                  = d[1].is_active ?? true

        const noCollectedSessions = d[1].no_collected_sessions || 0
        const totalMembers        = this.no_members || 0
        this.current_cycle = totalMembers > 0 ? Math.floor(noCollectedSessions / totalMembers) + 1 : 1

        if (!d[1].get_date_history) {
          switch (this.collecting_cycle) {
            case 'Daily':   this.date_history = this.getCurrentDate();  break
            case 'Weekly':  this.date_history = this.getCurrentWeekForDisplay();  break  // ✅ corrigé
            case 'Monthly': this.date_history = this.getCurrentMonth(); break
            default:        this.date_history = this.getCurrentDate()
          }
        } else {
          this.date_history = d[1].get_date_history
        }

        this.njangi_members              = d[2].group_members_contrib_info
        this.history_contributed_amount  = d[2].total_contributed
        this.history_expected_amount     = d[2].expected_contribution

        await Promise.all([
          this.getClientRequestedNjangiTransactions(group_id),
          this.getMemberPositions(group_id),
          this.getContribHistory(group_id),
        ])

        // ✅ Démarrer le countdown si nécessaire
        if (this.collecting_cycle === 'Weekly' && this.isGracePeriod) {
          this.startGracePeriodCountdown()
        }

      } catch (err) {
        this.handleError('Error loading group details', err)
      } finally {
        this.loading = false
      }
    },

    async getClientRequestedNjangiTransactions(group_id) {
      try { const r = await axios.get(`/api/v1/njangi/get/user/group/${group_id}/requested_transactions/`); this.njangiRequestedTransactions = r.data }
      catch (e) { console.error('Error loading transactions:', e) }
    },

    async getContribHistory(group_id) {
      try {
        const r = await axios.get(`/api/v1/njangi/get/user/group/members/${group_id}/${this.date_history}/`)
        this.njangi_members             = r.data.group_members_contrib_info
        this.history_contributed_amount = r.data.total_contributed
        this.history_expected_amount    = r.data.expected_contribution
      } catch (e) { this.handleError('Error loading contribution history', e) }
    },

    async getMemberPositions(group_id) {
      try {
        const r = await axios.get(`/api/v1/manager/njangi/group/${group_id}/member-positions/`)
        this.member_positions = r.data.sort((a, b) => a.position - b.position)
      } catch (e) { console.error('Error fetching member positions:', e) }
    },

goToCollectorPayment(collectorName, groupName) {
      if (!collectorName || collectorName === '—' || collectorName === 'To be activated') return
      this.$router.push({
        name: 'njangi.collector_payment',
        query: { collector: collectorName, group: groupName || '' },
      })
    },
    openPositionDialog(member)    { this.selected_member = member; this.new_position = member.position; this.position_error = ""; this.position_dialog = true },
    openMakeContribution(member)  { this.$router.push({ name: 'njangi.make_contribution', query: { userId: member.member_id, njangiId: this.njangi_id } }) },
    closePositionDialog()         { this.position_dialog = false; this.selected_member = null; this.new_position = null; this.position_error = "" },

    async updateMemberPosition() {
      if (!this.new_position || this.new_position < 1 || this.new_position > this.member_positions.length) {
        this.position_error = `Position must be between 1 and ${this.member_positions.length}`; return
      }
      this.position_error = ""; this.updating_position = true
      try {
        const r = await axios.post('/api/v1/manager/njangi/update-member-position/', {
          group_id: this.njangi_id, member_id: this.selected_member.member_id,
          old_position: this.selected_member.position, new_position: parseInt(this.new_position)
        })
        this.showSuccess(r.data.message || "Position updated successfully")
        await Promise.all([this.getMemberPositions(this.njangi_id), this.getNjangiGroupSetting(this.njangi_id)])
        this.closePositionDialog()
      } catch (e) { this.position_error = e.response?.data?.error || "Error updating position"; this.handleError('Error', e) }
      finally { this.updating_position = false }
    },

    getPositionTaker() {
      if (!this.new_position) return null
      return this.member_positions.find(m => m.position === parseInt(this.new_position) && m.id !== this.selected_member?.id)
    },

    confirmRemoveMember(member) { this.member_to_remove = member; this.remove_member_dialog = true },

    async removeMember() {
      this.removing_member = true
      try {
        const r = await axios.post('/api/v1/manager/njangi/remove-member/', {
          group_id: this.njangi_id, member_id: this.member_to_remove.member_id, position: this.member_to_remove.position
        })
        this.showSuccess(r.data.message || "Member removed successfully")
        await Promise.all([this.getMemberPositions(this.njangi_id), this.getNjangiGroupSetting(this.njangi_id)])
        this.remove_member_dialog = false; this.member_to_remove = null
      } catch (e) { this.handleError('Error removing member', e) }
      finally { this.removing_member = false }
    },

    async deactivateNjangi(group_id) {
      this.loading = true; this.delete_njangi = false
      try {
        const r = await axios.post(`/api/v1/manager/njangi/deactivate/${group_id}/`)
        this.showSuccess(r.data[0]?.text || "Group deactivated")
        this.resetDialogState(); await this.getNjangiGroups()
      } catch (e) { this.handleError('Error deactivating', e) }
      finally { this.loading = false }
    },

    async activateNjangi(group_id) {
      this.loading = true; this.activate_njangi = false
      try {
        const r = await axios.post(`/api/v1/manager/njangi/activate/${group_id}/`)
        this.showSuccess(r.data[0]?.text || "Group activated")
        this.resetDialogState(); await this.getNjangiGroups()
      } catch (e) { this.handleError('Error activating', e) }
      finally { this.loading = false }
    },

    async resetGroupCycle() {
      if (!this.njangi_id) return
      this.resetting_cycle = true
      try {
        const r = await axios.post(`/api/v1/manager/njangi/reset-cycle/${this.njangi_id}/`, { reason: this.reset_reason })
        this.showSuccess(`✅ Cycle reset to v${r.data.version}. First collector: position #${r.data.first_collector_position}`)
        this.reset_cycle_dialog = false; this.reset_reason = ''
        await this.getNjangiGroupSetting(this.njangi_id)
      } catch (e) { this.handleError('Error resetting cycle', e) }
      finally { this.resetting_cycle = false }
    },

    async loadCycleVersions() {
      this.loading_versions = true
      try {
        const r = await axios.get(`/api/v1/manager/njangi/group/${this.njangi_id}/cycle-versions/`)
        this.cycle_versions = r.data; this.show_cycle_history = true
      } catch (e) { this.handleError('Error loading history', e) }
      finally { this.loading_versions = false }
    },

    async goToChat(groupId) {
      try {
        const r = await axios.post('/api/v1/manager/list_id_njangi/', { group_id: groupId })
        this.$router.push({ name: 'chat.chat_group', params: { group_name: r.data.group_name, list_ids: r.data.list_ids.join('-') } })
      } catch (e) { this.handleError('Error redirecting to chat', e) }
    },

    resetDialogState() {
      Object.assign(this, {
        dialog: false, manage_positions: false, delete_njangi: false, activate_njangi: false,
        position_dialog: false, remove_member_dialog: false, reset_cycle_dialog: false,
        reset_reason: '', resetting_cycle: false, cycle_versions: [], show_cycle_history: false,
        selected_member: null, member_to_remove: null, new_position: null, position_error: "",
        member_positions: [], njangi_members: [], njangiRequestedTransactions: [],
        njangi_id: "", njangi_group_name: '', njangi_contact: '', njangi_group_description: '',
        contribution_amount: '', collecting_cycle: '', no_contributed: null, no_members: null,
        date_history: "", collection_date: "", start_date: "", start_date_raw: null,
        collection_activated: false, contributed_amount: "", expected_amount: "",
        current_collector: "", current_collect_position: null,
        history_contributed_amount: "", history_expected_amount: "",
        is_active: true, current_cycle: 1,
        show_invite_dialog: false, searchUsername: '', searchResults: [], searchingUsers: false, sendingInvitation: false,
        show_invitations_dialog: false, invitations: [], loading_invitations: false, cancelling_invitation: null,
        gracePeriodCountdown: { days: '00', hours: '00', minutes: '00', seconds: '00' },
        graceCountdownInterval: null,
      })
    },

    handleError(message, error) {
      console.error(message, error)
      const msg = error.response?.data?.error || error.message || "Unexpected error"
      this.$store.commit('setSnackBarMessage', `${message}: ${msg}`)
      this.$store.commit('setSnackBarColor', "red darken-4")
      this.$store.commit('activateSnackBar', true)
    },
    showSuccess(message) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', "success")
      this.$store.commit('activateSnackBar', true)
    },

    async openInvitationsDialog() { this.show_invitations_dialog = true; await this.getGroupInvitations() },
    async getGroupInvitations() {
      if (!this.njangi_id) return
      this.loading_invitations = true
      try { const r = await axios.get(`/api/v1/manager/njangi/groups/${this.njangi_id}/invitations/`); this.invitations = r.data }
      catch (e) { this.handleError('Error loading invitations', e) }
      finally { this.loading_invitations = false }
    },
    async cancelInvitation(membershipId) {
      if (!this.njangi_id) return
      this.cancelling_invitation = membershipId
      try {
        await axios.post(`/api/v1/manager/njangi/groups/${this.njangi_id}/invitations/${membershipId}/cancel/`)
        this.showSuccess('✅ Invitation cancelled')
        this.invitations = this.invitations.filter(inv => inv.membership_id !== membershipId)
      } catch (e) { this.handleError('Error cancelling invitation', e) }
      finally { this.cancelling_invitation = null }
    },

    getCollectorForPeriod(dateStr) {
      if (!this.start_date_raw || !this.no_members || this.no_members === 0)
        return { collector: null, position: null, previous: [] }
      const start    = new Date(this.start_date_raw)
      const selected = new Date(dateStr + (this.collecting_cycle === 'Monthly' ? '-01' : ''))
      if (isNaN(start) || isNaN(selected) || selected < start)
        return { collector: null, position: null, previous: [] }
      let sessionNumber = 0
      if (this.collecting_cycle === 'Daily') {
        sessionNumber = Math.floor((selected - start) / 86400000)
      } else if (this.collecting_cycle === 'Weekly') {
        const adj = d => { const x = new Date(d); x.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return x }
        sessionNumber = Math.max(0, Math.round((adj(selected) - adj(start)) / (7 * 86400000)))
      } else if (this.collecting_cycle === 'Monthly') {
        sessionNumber = Math.max(0, (selected.getFullYear() - start.getFullYear()) * 12 + (selected.getMonth() - start.getMonth()))
      } else {
        return { collector: null, position: null, previous: [] }
      }
      const total    = this.no_members
      const position = (sessionNumber % total) + 1
      const collector = this.member_positions.find(m => m.position === position) || null
      const previous  = []
      for (let i = 1; i <= Math.min(3, sessionNumber); i++) {
        const pp = ((sessionNumber - i) % total) + 1
        const pc = this.member_positions.find(m => m.position === pp)
        if (pc && pc.member_name !== collector?.member_name) previous.push(pc.member_name)
      }
      return { collector: collector?.member_name || null, position, previous }
    },

    // 🆕 Méthode pour le countdown de grâce
    startGracePeriodCountdown() {
      if (this.graceCountdownInterval) clearInterval(this.graceCountdownInterval)
      if (!this.isGracePeriod) return

      const tick = () => {
        const now = new Date()
        const dayOfWeek = now.getDay()
        const daysUntil = (WINDOW_OPEN_DAY - dayOfWeek + 7) % 7 || 7
        const nextOpen = new Date(now)
        nextOpen.setDate(now.getDate() + daysUntil)
        nextOpen.setHours(0, 0, 0, 0)
        const diff = nextOpen - now

        if (diff <= 0) {
          window.location.reload()
          return
        }

        const d = Math.floor(diff / (1000 * 60 * 60 * 24))
        const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
        const s = Math.floor((diff % (1000 * 60)) / 1000)

        this.gracePeriodCountdown = {
          days:    d.toString().padStart(2, '0'),
          hours:   h.toString().padStart(2, '0'),
          minutes: m.toString().padStart(2, '0'),
          seconds: s.toString().padStart(2, '0'),
        }
      }

      tick()
      this.graceCountdownInterval = setInterval(tick, 1000)
    },

    resetFilters() { this.selectedCycle = 'All'; this.selectedAmounts = []; this.search = '' },
  },

  beforeDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
    if (this.graceCountdownInterval) clearInterval(this.graceCountdownInterval)
  },
}
</script>



<style scoped>
/* ═══════════════════════════════════════════════════════════════
   AJOUTS POUR LA GRACE PERIOD (manager)
   ═══════════════════════════════════════════════════════════════ */
.mgr-grace-banner {
  background: linear-gradient(135deg, #bf360c, #e64a19);
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 4px 16px rgba(191,54,12,.25);
}
.mgr-grace-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.mgr-grace-left {
  display: flex;
  align-items: center;
}
.mgr-grace-title {
  font-size: 13px;
  font-weight: 800;
  color: white;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 3px;
}
.mgr-grace-chip {
  background: rgba(255,255,255,.2);
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: .3px;
}
.mgr-grace-sub {
  font-size: 11px;
  color: rgba(255,255,255,.8);
}
.mgr-grace-sub strong {
  color: white;
}
.mgr-grace-countdown {
  display: flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
}
.mgr-ct-block {
  text-align: center;
}
.mgr-ct-val {
  display: block;
  font-size: 18px;
  font-weight: 800;
  color: white;
  background: rgba(255,255,255,.15);
  border-radius: 5px;
  padding: 2px 5px;
  min-width: 30px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.mgr-ct-unit {
  font-size: 8px;
  color: rgba(255,255,255,.6);
  text-transform: uppercase;
}
.mgr-ct-sep {
  font-size: 16px;
  color: rgba(255,255,255,.4);
  margin-bottom: 8px;
}
@media (max-width: 599px) {
  .mgr-grace-inner      { flex-direction: column; gap: 8px; }
  .mgr-grace-countdown  { align-self: flex-start; }
  .mgr-ct-val           { font-size: 15px; min-width: 25px; }
}


/* ── ANIMATIONS ── */
@keyframes spinner  { to { transform: rotate(360deg); } }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
.spinner-anim { animation: spinner 1s linear infinite; }
.njangi-main-card > * { animation: fadeInUp 0.35s ease forwards; }

/* ── DIALOG ── */
.njangi-dialog-elevated {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 20px 48px rgba(0,0,0,0.22) !important;
}
.njangi-dialog { border-radius: 14px !important; overflow: hidden; }

/* Full-screen sur mobile : pas de border-radius */
@media (max-width: 599px) {
  .njangi-dialog-elevated { border-radius: 0 !important; }
}

/* ── HERO ── */
.njangi-hero-header {
  background: linear-gradient(135deg, var(--v-primary-base, #1976d2) 0%, #5c6bc0 60%, #7986cb 100%);
  position: relative;
}
.njangi-hero-header::before {
  content: '';
  position: absolute; inset: 0;
  background: radial-gradient(circle at 80% 30%, rgba(255,255,255,0.12) 0%, transparent 60%);
  pointer-events: none; z-index: 0;
}
.hero-overlay { position: relative; z-index: 1; padding-bottom: 16px !important; }
.hero-icon-wrap {
  width: 44px; height: 44px; border-radius: 12px;
  background: rgba(255,255,255,0.2);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.hero-title-block { flex: 1; min-width: 0; }
.close-btn { flex-shrink: 0 !important; transition: all 0.3s ease; }
.close-btn:hover { background: rgba(255,255,255,0.3) !important; transform: rotate(90deg); }
.status-chip { font-weight: 600 !important; }

/* ── ACTION BAR ── */
.njangi-action-bar {
  background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 1px solid rgba(0,0,0,0.06);
}
/* Scroll horizontal sur très petits écrans */
.action-scroll-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
@media (max-width: 360px) {
  .action-scroll-row { flex-wrap: nowrap; padding-bottom: 4px; }
}
.action-btn { font-weight: 600 !important; letter-spacing: 0.2px; transition: all 0.2s ease !important; flex-shrink: 0; }
.action-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(0,0,0,0.14) !important; }

/* ── INFO / COLLECTOR CARDS ── */
.info-card { transition: all 0.3s ease; border-color: rgba(0,0,0,0.08) !important; }
.nav-link { cursor: pointer; }
.nav-link:hover { text-decoration: underline; opacity: 0.85; }
.info-row  { display: flex; align-items: center; padding: 3px 0; }
.collector-card {
  background: linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%) !important;
  border-color: rgba(0,0,0,0.06) !important;
}
.collector-avatar {
  width: 52px; height: 52px; border-radius: 50%;
  background: linear-gradient(135deg, var(--v-primary-base, #1976d2), #5c6bc0);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 10px rgba(25,118,210,0.28);
  flex-shrink: 0;
}

/* ── STAT CARDS ── */
.stat-card {
  border-radius: 12px !important;
  border-color: rgba(0,0,0,0.06) !important;
  transition: all 0.25s ease;
}
.stat-card:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0,0,0,0.08) !important; }
.stat-success { background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%) !important; border-color: #a5d6a7 !important; }
.stat-error   { background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%) !important; border-color: #ef9a9a !important; }

/* ── PROGRESS ── */
.progress-animated { border-radius: 12px !important; }
.progress-animated .v-progress-linear__determinate { transition: width 0.8s cubic-bezier(0.4,0,0.2,1) !important; }

/* ── TRANSACTIONS ── */
.transaction-item { border: 1px solid #e0e0e0; background: white; transition: all 0.2s ease; }
.transaction-item:hover { transform: translateX(3px); box-shadow: 0 4px 10px rgba(0,0,0,0.07); }
.withdrawal-row { border-left: 4px solid #f44336 !important; }
.deposit-row    { border-left: 4px solid #4caf50 !important; }

/* ── MEMBER SECTION ── */
.member-header {
  background: linear-gradient(135deg, var(--v-primary-base, #1976d2) 0%, #5c6bc0 100%);
}
.date-selector-bar  { border-bottom: 1px solid rgba(0,0,0,0.06); }
.date-selector-wrapper, .week-selector-container, .month-selector-container { position: relative; width: 100%; }
.date-field .v-input__slot { border-radius: 10px !important; }

.stat-pill {
  display: inline-flex; align-items: center;
  padding: 6px 12px; border-radius: 50px;
}
.stat-pill.success { background: linear-gradient(135deg,#4caf50,#66bb6a); box-shadow: 0 4px 10px rgba(76,175,80,0.28); }
.stat-pill.error   { background: linear-gradient(135deg,#f44336,#ef5350); box-shadow: 0 4px 10px rgba(244,67,54,0.28); }

.member-list { padding: 0; }
.member-item { border: 1px solid transparent; background: white; transition: all 0.2s ease; }
.member-item:hover { transform: translateX(3px); box-shadow: 0 3px 12px rgba(0,0,0,0.07); }
.success-bg { background: linear-gradient(90deg,#e8f5e9 0%,#fff 100%) !important; border-left: 4px solid #4caf50 !important; }
.error-bg   { background: linear-gradient(90deg,#ffebee 0%,#fff 100%) !important; border-left: 4px solid #f44336 !important; }
.empty-state { opacity: 0.7; }

/* ── POSITIONS ── */
.manage-card { border-radius: 16px !important; overflow: hidden; }
.position-item { border: 1px solid #e0e0e0; background: white; transition: all 0.2s ease; }
.position-item:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.07); border-color: var(--v-primary-base, #1976d2); }
.current-collector-row { background: linear-gradient(90deg,#e8f5e9 0%,#fff 100%) !important; border-left: 4px solid #4caf50 !important; }

/* ── CONFIRM ── */
.confirm-card { border-radius: 16px !important; overflow: hidden; }
.confirm-header { padding: 24px 20px; text-align: center; display: flex; flex-direction: column; align-items: center; }
.confirm-header.error   { background: linear-gradient(135deg,#f44336,#e53935); }
.confirm-header.success { background: linear-gradient(135deg,#4caf50,#43a047); }

/* ── VERSION HISTORY ── */
.version-card { transition: all 0.2s ease; }
.version-card:hover { box-shadow: 0 4px 10px rgba(0,0,0,0.07) !important; }
.current-version { border: 2px solid #4caf50 !important; background: #f1f8e9 !important; }

/* ── INVITE / INVITATION ITEMS ── */
.user-result-item  { background: white; transition: all 0.2s ease; }
.user-result-item:hover { background: #f5f5f5; box-shadow: 0 3px 10px rgba(0,0,0,0.07); border-color: var(--v-primary-base) !important; }
.invitation-item   { background: white; transition: all 0.2s ease; }
.invitation-item:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.06); border-color: var(--v-primary-base) !important; }

/* ── LOADING ── */
.loading-card-elevated { border-radius: 16px !important; min-height: 220px; display: flex; align-items: center; justify-content: center; }

/* ── TABLE ── */
.table-rounded { border-radius: 12px !important; overflow: hidden; }

/* ── SCROLLBARS ── */
::-webkit-scrollbar       { width: 5px; height: 5px; }
::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 10px; }
::-webkit-scrollbar-thumb:hover { background: #a1a1a1; }

/* ── RESPONSIVE ── */
@media (max-width: 599px) {
  /* Dialog full-screen */
  .njangi-main-card { border-radius: 0 !important; }

  /* Hero plus compact */
  .hero-icon-wrap { width: 36px; height: 36px; border-radius: 10px; }

  /* Stats : texte plus petit */
  .stat-card .text-h6 { font-size: 1.1rem !important; }
  .stat-card .caption { font-size: 10px !important; }

  /* Stat pills plus compactes */
  .stat-pill { padding: 5px 8px; }
  .stat-pill span { font-size: 11px !important; }

  /* Members list : pas de transform sur mobile (évite les glitches) */
  .member-item:hover { transform: none; }

  /* Boutons de la table */
  .v-data-table td { padding: 6px 8px !important; }

  /* Collector card : mise en ligne sur mobile */
  .collector-card { flex-direction: row !important; text-align: left !important; }
  .collector-avatar { width: 40px !important; height: 40px !important; }
}

@media (max-width: 360px) {
  .action-btn { font-size: 11px !important; }
  .hero-icon-wrap { display: none; }
}
</style>